export interface ZodiacAnimal {
    name: string;
    emoji: string;
    element: string;
    yinYang: 'Yin' | 'Yang';
    keywords: string[];
    description: string;
    strengths: string[];
    weaknesses: string[];
    compatibility: string[];
    luckyNumbers: number[];
    luckyColors: string[];
}

// Animal order: Rat(0) → Ox(1) → Tiger(2) → Rabbit(3) → Dragon(4) → Snake(5) →
//               Horse(6) → Goat(7) → Monkey(8) → Rooster(9) → Dog(10) → Pig(11)
export const zodiacAnimals: ZodiacAnimal[] = [
    {
        name: 'Rat',
        emoji: '🐀',
        element: 'Water',
        yinYang: 'Yang',
        keywords: ['Clever', 'Quick-witted', 'Resourceful', 'Versatile'],
        description: 'People born in the Year of the Rat are clever, quick-witted, and resourceful. They have strong intuition and can adapt to any environment. Rats are curious and observant, often noticing details that others miss. They are excellent at saving and investing, making them financially savvy.',
        strengths: ['Intelligent', 'Adaptable', 'Charming', 'Thrifty'],
        weaknesses: ['Stubborn', 'Critical', 'Greedy', 'Anxious'],
        compatibility: ['Dragon', 'Monkey', 'Ox'],
        luckyNumbers: [2, 3],
        luckyColors: ['Blue', 'Gold', 'Green']
    },
    {
        name: 'Ox',
        emoji: '🐂',
        element: 'Earth',
        yinYang: 'Yin',
        keywords: ['Diligent', 'Dependable', 'Strong', 'Determined'],
        description: 'Those born in the Year of the Ox are known for their diligence, dependability, and strength. They are honest and patient, approaching tasks with methodical determination. Oxen are natural leaders who inspire trust through their integrity and hard work.',
        strengths: ['Honest', 'Patient', 'Reliable', 'Hardworking'],
        weaknesses: ['Stubborn', 'Conservative', 'Materialistic', 'Rigid'],
        compatibility: ['Rat', 'Snake', 'Rooster'],
        luckyNumbers: [1, 4],
        luckyColors: ['White', 'Yellow', 'Green']
    },
    {
        name: 'Tiger',
        emoji: '🐅',
        element: 'Wood',
        yinYang: 'Yang',
        keywords: ['Brave', 'Confident', 'Competitive', 'Unpredictable'],
        description: 'People born in the Year of the Tiger are brave, confident, and competitive. They love challenges and adventure, often taking the lead in difficult situations. Tigers are charismatic and authoritative, commanding respect wherever they go.',
        strengths: ['Courageous', 'Enthusiastic', 'Generous', 'Confident'],
        weaknesses: ['Reckless', 'Impatient', 'Arrogant', 'Short-tempered'],
        compatibility: ['Dragon', 'Horse', 'Pig'],
        luckyNumbers: [1, 3, 4],
        luckyColors: ['Blue', 'Gray', 'Orange']
    },
    {
        name: 'Rabbit',
        emoji: '🐇',
        element: 'Wood',
        yinYang: 'Yin',
        keywords: ['Gentle', 'Quiet', 'Elegant', 'Alert'],
        description: 'Those born in the Year of the Rabbit are gentle, quiet, and elegant. They are kind-hearted and have refined taste. Rabbits avoid conflict and prefer peaceful environments. They are excellent at creating beauty and harmony in their surroundings.',
        strengths: ['Compassionate', 'Diplomatic', 'Artistic', 'Prudent'],
        weaknesses: ['Timid', 'Pessimistic', 'Secretive', 'Detached'],
        compatibility: ['Goat', 'Pig', 'Dog'],
        luckyNumbers: [3, 4, 6],
        luckyColors: ['Red', 'Pink', 'Purple', 'Blue']
    },
    {
        name: 'Dragon',
        emoji: '🐉',
        element: 'Earth',
        yinYang: 'Yang',
        keywords: ['Confident', 'Intelligent', 'Enthusiastic', 'Ambitious'],
        description: 'People born in the Year of the Dragon are confident, intelligent, and ambitious. The Dragon is the most powerful and auspicious sign in the Chinese zodiac. Dragons are natural leaders who attract success and admiration. They are fearless and driven to achieve greatness.',
        strengths: ['Charismatic', 'Successful', 'Talented', 'Lucky'],
        weaknesses: ['Aggressive', 'Demanding', 'Egotistical', 'Intolerant'],
        compatibility: ['Rat', 'Tiger', 'Snake'],
        luckyNumbers: [1, 6, 7],
        luckyColors: ['Gold', 'Silver', 'Gray']
    },
    {
        name: 'Snake',
        emoji: '🐍',
        element: 'Fire',
        yinYang: 'Yin',
        keywords: ['Wise', 'Mysterious', 'Intuitive', 'Elegant'],
        description: 'Those born in the Year of the Snake are wise, mysterious, and intuitive. They are deep thinkers who rely on their intuition to guide them. Snakes are graceful and sophisticated, often keeping their thoughts and feelings private.',
        strengths: ['Wise', 'Elegant', 'Perceptive', 'Determined'],
        weaknesses: ['Jealous', 'Suspicious', 'Secretive', 'Vengeful'],
        compatibility: ['Dragon', 'Rooster', 'Ox'],
        luckyNumbers: [2, 8, 9],
        luckyColors: ['Black', 'Red', 'Yellow']
    },
    {
        name: 'Horse',
        emoji: '🐎',
        element: 'Fire',
        yinYang: 'Yang',
        keywords: ['Energetic', 'Independent', 'Cheerful', 'Free-spirited'],
        description: 'People born in the Year of the Horse are energetic, independent, and cheerful. They love freedom and adventure, always seeking new experiences. Horses are social and popular, easily making friends wherever they go.',
        strengths: ['Enthusiastic', 'Independent', 'Witty', 'Warm-hearted'],
        weaknesses: ['Impatient', 'Self-centered', 'Reckless', 'Moody'],
        compatibility: ['Tiger', 'Goat', 'Dog'],
        luckyNumbers: [2, 3, 7],
        luckyColors: ['Yellow', 'Green', 'Purple']
    },
    {
        name: 'Goat',
        emoji: '🐐',
        element: 'Earth',
        yinYang: 'Yin',
        keywords: ['Gentle', 'Calm', 'Creative', 'Sympathetic'],
        description: 'Those born in the Year of the Goat are gentle, calm, and creative. They are kind-hearted and deeply sympathetic to others. Goats have artistic souls and appreciate beauty in all forms. They prefer harmony and avoid confrontation.',
        strengths: ['Gentle', 'Creative', 'Resilient', 'Compassionate'],
        weaknesses: ['Indecisive', 'Passive', 'Pessimistic', 'Dependent'],
        compatibility: ['Rabbit', 'Horse', 'Pig'],
        luckyNumbers: [2, 7],
        luckyColors: ['Brown', 'Red', 'Purple']
    },
    {
        name: 'Monkey',
        emoji: '🐒',
        element: 'Metal',
        yinYang: 'Yang',
        keywords: ['Clever', 'Curious', 'Playful', 'Mischievous'],
        description: 'People born in the Year of the Monkey are clever, curious, and playful. They have quick minds and love to learn new things. Monkeys are excellent problem-solvers who can find creative solutions to any challenge.',
        strengths: ['Intelligent', 'Witty', 'Innovative', 'Sociable'],
        weaknesses: ['Cunning', 'Jealous', 'Selfish', 'Restless'],
        compatibility: ['Rat', 'Dragon', 'Snake'],
        luckyNumbers: [4, 9],
        luckyColors: ['White', 'Blue', 'Gold']
    },
    {
        name: 'Rooster',
        emoji: '🐓',
        element: 'Metal',
        yinYang: 'Yin',
        keywords: ['Observant', 'Hardworking', 'Confident', 'Courageous'],
        description: 'Those born in the Year of the Rooster are observant, hardworking, and confident. They are perfectionists who take pride in their appearance and achievements. Roosters are honest and straightforward, always speaking their mind.',
        strengths: ['Honest', 'Punctual', 'Confident', 'Motivated'],
        weaknesses: ['Critical', 'Vain', 'Argumentative', 'Impatient'],
        compatibility: ['Ox', 'Snake', 'Dragon'],
        luckyNumbers: [5, 7, 8],
        luckyColors: ['Gold', 'Brown', 'Yellow']
    },
    {
        name: 'Dog',
        emoji: '🐕',
        element: 'Earth',
        yinYang: 'Yang',
        keywords: ['Loyal', 'Honest', 'Courageous', 'Responsible'],
        description: 'People born in the Year of the Dog are loyal, honest, and courageous. They are faithful friends who will stand by you through thick and thin. Dogs have a strong sense of justice and always fight for what is right.',
        strengths: ['Loyal', 'Brave', 'Sincere', 'Helpful'],
        weaknesses: ['Stubborn', 'Anxious', 'Pessimistic', 'Judgmental'],
        compatibility: ['Rabbit', 'Tiger', 'Horse'],
        luckyNumbers: [3, 4, 9],
        luckyColors: ['Red', 'Green', 'Purple']
    },
    {
        name: 'Pig',
        emoji: '🐖',
        element: 'Water',
        yinYang: 'Yin',
        keywords: ['Compassionate', 'Generous', 'Diligent', 'Sincere'],
        description: 'Those born in the Year of the Pig are compassionate, generous, and sincere. They have kind hearts and enjoy helping others. Pigs love life and all its pleasures, often bringing joy and positivity to those around them.',
        strengths: ['Generous', 'Caring', 'Optimistic', 'Honest'],
        weaknesses: ['Naive', 'Materialistic', 'Lazy', 'Gullible'],
        compatibility: ['Tiger', 'Rabbit', 'Goat'],
        luckyNumbers: [2, 5, 8],
        luckyColors: ['Yellow', 'Gray', 'Brown', 'Gold']
    }
];

// Element cycle repeats every 10 years (2 years per element)
// Wood → Fire → Earth → Metal → Water
export const elements = ['Wood', 'Fire', 'Earth', 'Metal', 'Water'];

export function getElementForYear(year: number): string {
    // Element changes every 2 years starting with Wood at years ending in 4-5
    const elementIndex = Math.floor(((year - 4) % 10) / 2);
    return elements[elementIndex];
}
